import React from "react";

/** Palette (matches Rex Counsel) */
const COLORS = {
  pale: "#95B4CC",
  accent: "#6B8BA4",
  primary: "#2B4C65",
  ink: "#1D1C22",
  white: "#FFFFFF",
};

/** Small helper for gradient-borders without plugins */
function GradientCard({ children }) {
  return (
    <div className="rounded-2xl p-[1px]" style={{
      background: `linear-gradient(135deg, ${COLORS.pale}, ${COLORS.accent})`
    }}>
      <div className="rounded-2xl bg-white/90 backdrop-blur-sm h-full">{children}</div>
    </div>
  );
}

export default function WhereHowSection() {
  return (
    <section className="relative py-14 sm:py-16">
      {/* subtle background wash */}
      <div className="absolute inset-0 -z-10" style={{ background: `linear-gradient(180deg, rgba(149,180,204,0.08), rgba(107,139,164,0.06), rgba(255,255,255,0))` }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Heading */}
        <div className="mb-8">
          <div className="text-[11px] tracking-widest uppercase text-slate-500 mb-1">
            Capability & Approach
          </div>
          <h2 className="text-2xl md:text-3xl font-semibold" style={{ color: COLORS.ink }}>
            Where we work • How we work
          </h2>
          <div className="h-px mt-2 bg-gradient-to-r from-[var(--pale)] via-[var(--accent)] to-transparent"
               style={{ "--pale": COLORS.pale, "--accent": COLORS.accent }} />
        </div>

        {/* Main two-column layout */}
        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Left: Jurisdictions & Forums */}
          <div className="lg:col-span-7">
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {/* India */}
              <GradientCard>
                <div className="p-4 h-full flex flex-col">
                  <div className="text-sm font-semibold mb-1" style={{ color: COLORS.primary }}>India</div>
                  <div className="text-xs text-slate-600 mb-3">Forums: Supreme Court • High Courts • Tribunals</div>
                  <ul className="text-sm text-slate-800 space-y-1 mt-auto">
                    <li>Immigration & Human Rights</li>
                    <li>Cross-Border Litigation</li>
                    <li>Advisory • Filings • Hearings</li>
                  </ul>
                </div>
              </GradientCard>

              {/* United Kingdom */}
              <GradientCard>
                <div className="p-4 h-full flex flex-col">
                  <div className="text-sm font-semibold mb-1" style={{ color: COLORS.primary }}>United Kingdom</div>
                  <div className="text-xs text-slate-600 mb-3">Forums: Royal Courts of Justice • Tribunals</div>
                  <ul className="text-sm text-slate-800 space-y-1 mt-auto">
                    <li>Visa & Immigration Strategy</li>
                    <li>Asylum & Human Rights</li>
                    <li>Commercial Liaison</li>
                  </ul>
                </div>
              </GradientCard>

              {/* United States */}
              <GradientCard>
                <div className="p-4 h-full flex flex-col">
                  <div className="text-sm font-semibold mb-1" style={{ color: COLORS.primary }}>United States</div>
                  <div className="text-xs text-slate-600 mb-3">Forums: Federal & State Agencies/Courts</div>
                  <ul className="text-sm text-slate-800 space-y-1 mt-auto">
                    <li>Waivers & Complex Visa Issues</li>
                    <li>Cross-Border Coordination</li>
                    <li>Advisory • Filings</li>
                  </ul>
                </div>
              </GradientCard>
            </div>
          </div>

          {/* Right: The Rex Method */}
          <div className="lg:col-span-5">
            <div className="rounded-2xl border bg-white shadow-sm"
                 style={{ borderColor: COLORS.pale }}>
              <div className="p-5 sm:p-6">
                <div className="text-sm font-semibold mb-3" style={{ color: COLORS.primary }}>
                  The Rex Method
                </div>

                <ol className="space-y-4">
                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                         style={{ background: COLORS.pale, color: COLORS.primary }}>
                      1
                    </div>
                    <div>
                      <div className="font-medium" style={{ color: COLORS.ink }}>Listen</div>
                      <div className="text-sm text-slate-600">Understand the person behind the paperwork.</div>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                         style={{ background: COLORS.pale, color: COLORS.primary }}>
                      2
                    </div>
                    <div>
                      <div className="font-medium" style={{ color: COLORS.ink }}>Map the law</div>
                      <div className="text-sm text-slate-600">Research, forum & jurisdiction, timelines, risk.</div>
                    </div>
                  </li>

                  <li className="flex gap-3">
                    <div className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold"
                         style={{ background: COLORS.pale, color: COLORS.primary }}>
                      3
                    </div>
                    <div>
                      <div className="font-medium" style={{ color: COLORS.ink }}>Execute</div>
                      <div className="text-sm text-slate-600">Filings ready for scrutiny. Proactive follow-ups.</div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>
          </div>
        </div>

        {/* Image strip (India • UK • US) */}
        <div className="mt-10 rounded-3xl overflow-hidden">
          <div className="grid md:grid-cols-3 h-[260px] md:h-[360px]">
            {/* INDIA */}
            <div className="relative">
              <img
                src="/home/uk-rcj.jpg"
                alt="Supreme Court of India"
                className="absolute inset-0 w-full h-full object-cover object-center"
                onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
              />
              <div className="absolute inset-0" style={{
                background: `linear-gradient(135deg, rgba(107,139,164,0.55), rgba(43,76,101,0.65))`
              }} />
              <div className="absolute bottom-3 left-4 text-sm font-medium text-white">United Kingdoms</div>
            </div>

            {/* UK */}
            <div className="relative">
              <img
                src="/home/india-sc.jpg" 
                alt="Royal Courts of Justice, London"
                className="absolute inset-0 w-full h-full object-cover object-center"
                onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
              />
              <div className="absolute inset-0" style={{
                background: `linear-gradient(135deg, rgba(107,139,164,0.55), rgba(43,76,101,0.65))`
              }} />
              <div className="absolute bottom-3 left-4 text-sm font-medium text-white">India</div>
            </div>

            {/* US */}
            <div className="relative">
              <img
                src="/home/us-supreme-court.jpg"
                alt="U.S. Supreme Court"
                className="absolute inset-0 w-full h-full object-cover object-center"
                onError={(e) => { e.currentTarget.style.opacity = 0.2; }}
              />
              <div className="absolute inset-0" style={{
                background: `linear-gradient(135deg, rgba(107,139,164,0.55), rgba(43,76,101,0.65))`
              }} />
              <div className="absolute bottom-3 left-4 text-sm font-medium text-white">United States</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
